import { Box, Stack, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { showErrorToast } from "../../../../../components/common/ShowToast";
import { getFileExtension } from "../utils/getFileExtension";
import {
  ErrorRow,
  FileComment,
  processCSVFile,
  processHeadings,
} from "../utils/processCSVFile";
import {
  ErrorComment,
  extractExcelFileHeading,
  processExcelComments,
} from "../utils/processExcelComment";
import { extractJsonHeadings, processJSONFile } from "../utils/processJSONFile";
import { useParams } from "react-router-dom";
import { ReactComponent as CloudIcon } from "./../../../../../assets/cloud-icon.svg";
import { MappingFileCommentsKeys } from "./MappingFileCommentsKeys";
import { VerifyFileComments } from "./ConfirmFileComments";

interface Props {
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  changeModalProps: (toDefault: boolean) => void;
}

type displayModeType = "file-upload" | "confirm-data" | "mapping";

export const CommentsFileUploadSection: React.FC<Props> = ({
  setOpen,
  changeModalProps,
}) => {
  const { id } = useParams<{ id: string }>();
  const commentsFileRef = useRef<HTMLInputElement | null>(null);
  const currentFile = useRef<File | null>(null);
  const [fileComments, setFileComments] = useState<FileComment[]>([]);
  const [displayMode, setDisplayMode] =
    useState<displayModeType>("file-upload");
  const [errorComments, setErrorComments] = useState<ErrorComment[]>([]);
  const [headingsFromFile, setHeadingFromFile] = useState<string[]>([]);
  const [fieldsMapped, setFieldsMapped] = useState({
    last_name: "",
    first_name: "",
    email: "",
    phone_number: "",
    comment: "",
  });

  useEffect(() => {
    if (displayMode === "file-upload" || displayMode === "mapping") {
      changeModalProps(true);
    }
  }, [displayMode]);

  useEffect(() => {
    console.log(fieldsMapped);
  }, [fieldsMapped]);

  const updateFieldsMapped = (
    name: keyof typeof fieldsMapped,
    value: string
  ) => {
    setFieldsMapped((prev) => ({ ...prev, [name]: value }));
  };

  const openFileBrowseWindow = () => {
    commentsFileRef.current?.click();
  };

  const changed = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) {
      showErrorToast("No file uploaded");

      return;
    }
    const file = e.target.files[0];
    const fileType = getFileExtension(file);

    if (fileType === null || file === undefined) {
      showErrorToast("Failed to get file extension.Try uploading file again");
      return;
    }

    currentFile.current = file;
    const headings = await processHeadings(file);
    setHeadingFromFile(headings);

    if (fileType === "xlsx") {
      const excelHeadings = await extractExcelFileHeading(file);
      if (typeof excelHeadings === "string") {
        return;
      }
      setHeadingFromFile(excelHeadings);
    }

    if (fileType === "csv") {
      const excelHeadings = await processHeadings(file);
      if (typeof excelHeadings === "string") {
        return;
      }
      setHeadingFromFile(excelHeadings);
    }

    if (fileType === "json") {
      const jsonHeadings = await extractJsonHeadings(file);
      if (typeof jsonHeadings === "string") {
        return;
      }
      setHeadingFromFile(jsonHeadings);
    }

    setDisplayMode("mapping");
  };

  const updateCommentsDataFromFile = (
    comments: FileComment[],
    errComments: ErrorComment[]
  ) => {
    setFileComments(comments);
    setErrorComments(errComments);
    changeModalProps(false);
    setDisplayMode("confirm-data");
  };

  const onBack = () => {
    setDisplayMode("file-upload");
  };

  const readCommentsFile = async () => {
    // check if required fields have been mapped
    const fieldsMappedKeys = Object.keys(
      fieldsMapped
    ) as (keyof typeof fieldsMapped)[];
    for (let i = 0; i < fieldsMappedKeys.length; i++) {
      const currentKey = fieldsMappedKeys[i];
      if (fieldsMapped[currentKey].length === 0) {
        showErrorToast("You need to map all the fields");
        return;
      }
    }

    // All required fields are mapped, continue with reading file...

    if (currentFile.current === null) {
      showErrorToast("No file selected");
      return;
    }

    const fileType = getFileExtension(currentFile.current);

    if (fileType === "xlsx") {
      const fileData = await processExcelComments(
        id,
        currentFile.current,
        fieldsMapped
      );
      fileData.errorOccured
        ? showErrorToast(fileData.message)
        : updateCommentsDataFromFile(fileData.comments, []);
    }

    if (fileType === "csv") {
      const fileData = await processCSVFile(
        currentFile.current,
        `${id}`,
        fieldsMapped
      );
      fileData.errorOccured
        ? showErrorToast(fileData.message)
        : updateCommentsDataFromFile(fileData.comments, fileData.errorComments);
    }

    if (fileType === "json") {
      const fileData = await processJSONFile(
        currentFile.current,
        `${id}`,
        fieldsMapped
      );
      fileData.errorOccured
        ? showErrorToast(fileData.message)
        : updateCommentsDataFromFile(fileData.comments, fileData.errorComments);
    }
  };

  const removeErrorRecord = (id: number) => {
    setErrorComments((prev) => prev.filter((comment) => comment.id !== id));
  };

  return (
    <Box sx={{ width: "100%", minHeight: 300, position: "relative" }}>
      {displayMode === "file-upload" && (
        <>
          <input
            ref={commentsFileRef}
            style={{ width: "100%", height: "100%", display: "none" }}
            type="file"
            accept=".xlsx, .xls, .csv, .json"
            onChange={(e) => changed(e)}
          />

          <Stack
            onClick={openFileBrowseWindow}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: -1,
              width: "100%",
              height: 300,
              "&:hover": {
                cursor: "pointer",
                backgroundColor: "#F0F4F8",
                borderRadius: "0 0 1.1rem 1.1rem",
              },
            }}
          >
            <Box sx={{ width: "fit-content", marginInline: "auto" }}>
              <CloudIcon
                style={{
                  height: 60,
                  width: 60,
                }}
              />
            </Box>
            <Typography variant="body1" textAlign={"center"} mb={1}>
              Drag and drop your file <br /> or click to upload
            </Typography>
          </Stack>
        </>
      )}

      {displayMode === "mapping" && (
        <MappingFileCommentsKeys
          setOpen={setOpen}
          headingsFromFile={headingsFromFile}
          updateFieldsMapped={updateFieldsMapped}
          fieldsMapped={fieldsMapped}
          onBack={onBack}
          readCommentsFile={readCommentsFile}
        />
      )}

      {displayMode === "confirm-data" && (
        <VerifyFileComments
          fileComments={fileComments}
          setFileComments={setFileComments}
          fetchComments={null}
          commentsFileRef={commentsFileRef}
          setOpen={setOpen}
          errorComments={errorComments}
          removeErrorRecord={removeErrorRecord}
          changed={changed}
        />
      )}
    </Box>
  );
};
